import { WidgetsInterface } from '~constants/WidgetsNames';

import { initWidgetShadowDom } from './createWidget';

export interface WidgetController {
  setOpen: (isOpen: boolean) => void;
}

export interface PurchaseWidgetPrefill {
  productCategoryShortname?: string;
  validityCategoryShortname?: string;
  date?: string;
  participants?: string;
  contractorSlug?: string;
}

export interface PurchaseWidgetHTMLElement extends HTMLElement {
  _controller: WidgetController;
  open: boolean;
  show(): void;
  close(): void;
  toggle(): void;
  openWith(prefill?: PurchaseWidgetPrefill): void;
}

export const PURCHASE_WIDGET_OBSERVED_DATA_ATTRIBUTES = [
  'data-product-category-shortname',
  'data-validity-category-shortname',
  'data-date',
  'data-participants',
  'data-contractor-slug',
] as const;

export const PURCHASE_WIDGET_DATA_CHANGE_EVENT = 'purchase-widget:datachange';

/**
 * Register the purchase-widget custom element.
 *
 * Follows the same API contract as the native <dialog> element:
 * - Property `open` (getter/setter) reflected to an HTML attribute
 * - Methods `.show()`, `.close()`, `.toggle()`
 * - Dispatches a `toggle` CustomEvent with `{ oldState, newState }`
 *
 * Plus a purchase-specific imperative API to atomically reset + set
 * prefill data-* attributes and open the drawer:
 * - `.openWith(prefill?)`
 */
export const createPurchaseWidget = (widget: WidgetsInterface) => {
  class PurchaseWidgetElement extends HTMLElement {
    _controller: WidgetController = { setOpen: () => {} };
    private _dataChangePending = false;

    static get observedAttributes() {
      return ['open', ...PURCHASE_WIDGET_OBSERVED_DATA_ATTRIBUTES];
    }

    connectedCallback() {
      initWidgetShadowDom(this, widget);
    }

    get open(): boolean {
      return this.hasAttribute('open');
    }

    set open(value: boolean) {
      this.toggleAttribute('open', value);
    }

    attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
      if (oldValue === newValue) return;

      if (name === 'open') {
        const isOpen = newValue !== null;
        this._controller.setOpen(isOpen);

        this.dispatchEvent(
          new CustomEvent('toggle', {
            bubbles: true,
            detail: {
              newState: isOpen ? 'open' : 'closed',
              oldState: isOpen ? 'closed' : 'open',
            },
          }),
        );
        return;
      }

      // Coalesce bursts of data-* mutations (typical of openWith or a sequence
      // of setAttribute calls) into a single dispatch per microtask, so the
      // React flow remounts only once per batch.
      if (this._dataChangePending) return;
      this._dataChangePending = true;
      queueMicrotask(() => {
        this._dataChangePending = false;
        this.dispatchEvent(new CustomEvent(PURCHASE_WIDGET_DATA_CHANGE_EVENT));
      });
    }

    show() {
      this.open = true;
    }

    close() {
      this.open = false;
    }

    toggle() {
      this.open = !this.open;
    }

    openWith(prefill: PurchaseWidgetPrefill = {}) {
      for (const attr of PURCHASE_WIDGET_OBSERVED_DATA_ATTRIBUTES) {
        this.removeAttribute(attr);
      }
      if (prefill.productCategoryShortname !== undefined) {
        this.dataset.productCategoryShortname = prefill.productCategoryShortname;
      }
      if (prefill.validityCategoryShortname !== undefined) {
        this.dataset.validityCategoryShortname = prefill.validityCategoryShortname;
      }
      if (prefill.date !== undefined) {
        this.dataset.date = prefill.date;
      }
      if (prefill.participants !== undefined) {
        this.dataset.participants = prefill.participants;
      }
      if (prefill.contractorSlug !== undefined) {
        this.dataset.contractorSlug = prefill.contractorSlug;
      }
      this.open = true;
    }
  }

  const tagName = `${widget.id}-widget`;
  if (!customElements.get(tagName)) {
    customElements.define(tagName, PurchaseWidgetElement);
  }
};
